import React, { useState, useEffect, useCallback } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { trpc } from 'src/trpc';
import { DataTable } from 'src/components/table/DataTable';
import { SortingState } from '@tanstack/react-table';
import { NewAddOnModal } from 'pages/Settings/add-on/NewAddOnModal';
import { EditAddOnModal } from 'pages/Settings/add-on/EditAddOnModal';
import { addOnColumns } from 'pages/Settings/add-on/AddOnColumns';
import { AddOn } from 'src/trpc';
import { Search } from 'src/components/Search';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from 'src/components/Debounce';
import { useOrganization } from 'src/context/OrganizationContext';
import { Loader } from 'src/components/Loader';

interface AddOnWithActions extends AddOn {
  onEdit: (_addOn: AddOn) => void;
}

const AddOnDashboard: React.FC = () => {
  const [addOns, setAddOns] = useState<AddOnWithActions[]>([]);
  const [editingAddOn, setEditingAddOn] = useState<AddOn | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [params, setParams] = useSearchParams({
    offset: '0',
    limit: '50',
    search: '',
  });

  const [search, setSearch] = useState(params.get('search') ?? '');
  const debouncedSearch = useDebounce(search, 500);

  const offset = parseInt(params.get('offset')!);
  const limit = parseInt(params.get('limit')!);
  const currentPage = offset === 0 ? 1 : offset / limit + 1;

  // Use organization context for org-dependent queries
  const { organizationId, isResolved, isOrganizationSwitching, setIsOrganizationSwitching } =
    useOrganization();

  const { data, isLoading, refetch, isFetching } = trpc.admin.addOn.getAll.useQuery(
    {
      offset,
      limit,
      search: params.get('search') || '',
    },
    {
      placeholderData: keepPreviousData,
      enabled: isResolved && !!organizationId,
    },
  );

  // Reset organization switching state when fetch completes
  useEffect(() => {
    if (isOrganizationSwitching && !isFetching) {
      setIsOrganizationSwitching(false);
    }
  }, [isOrganizationSwitching, isFetching, setIsOrganizationSwitching]);

  useEffect(() => {
    if (data) {
      const addOnsWithEditAction: AddOnWithActions[] = data.addOns.map((addOn) => ({
        ...addOn,
        onEdit: (addOn: AddOn) => setEditingAddOn(addOn),
      }));
      setAddOns(addOnsWithEditAction);
    }
  }, [data]);

  const updateSearchParams = useCallback(
    (searchValue: string) => {
      const newParams = new URLSearchParams(params);
      newParams.set('search', searchValue);
      newParams.set('offset', '0');
      setParams(newParams);
    },
    [params, setParams],
  );

  useEffect(() => {
    const currentSearch = params.get('search') ?? '';
    if (debouncedSearch !== currentSearch) {
      updateSearchParams(debouncedSearch);
    }
  }, [debouncedSearch, params, updateSearchParams]);

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const handlePageChange = useCallback(
    (change: number) => {
      const newParams = new URLSearchParams(params);
      newParams.set('offset', (offset + change * limit).toString());
      setParams(newParams);
    },
    [offset, limit, params, setParams],
  );

  if (!isResolved || isOrganizationSwitching) {
    return <Loader title="Getting add-ons" />;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold mb-4">Add-ons</h2>
      <div className="flex justify-between items-center space-x-4">
        <div className="flex-grow">
          <Search
            value={search}
            onChange={handleSearch}
            placeholder="Search add-ons by title or description"
            isLoading={isLoading}
          />
        </div>
        <NewAddOnModal onSuccess={() => refetch()} />
      </div>
      <div className="flex flex-col h-full">
        <DataTable
          columns={addOnColumns}
          data={addOns}
          sorting={sorting}
          onSortingChange={setSorting}
          onPageChange={handlePageChange}
          pageCount={Math.ceil((data?.total || 0) / limit)}
          currentPage={currentPage}
          total={data?.total || 0}
          resourceName="add-ons"
          offset={offset}
          limit={limit}
          onRowClick={(row) => {
            setEditingAddOn(row);
          }}
        />
      </div>
      <EditAddOnModal
        addOn={editingAddOn}
        onClose={() => setEditingAddOn(null)}
        onSuccess={() => {
          refetch();
          setEditingAddOn(null);
        }}
      />
    </div>
  );
};

export default AddOnDashboard;
